import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import useTransformLinkedEntitiesResponse from 'src/features/pricingPlan/api/linkedEntities/useTransformLinkedEntitiesResponse';
import { useGetPricingPlan } from 'src/features/pricingPlan/api/useGetPricingPlan';
import PricingPlanForm from 'src/features/pricingPlan/components/form/PricingPlanForm';
import { pricingPlanFormSchema, PricingPlanFormSchemaValues } from 'src/features/pricingPlan/utils/formSchema';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { CAPPING, PRICING_PLAN_STATUS, PricingPlan } from 'src/features/pricingPlan/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';

interface PricingPlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPricingPlan?: PricingPlan;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: PricingPlanFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
    // When the plan is published, only name/description
    // are allowed to be edited, so we use a patch partial update.
    mutatePartial?: (
        data: Partial<PricingPlanFormSchemaValues>,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
    isNewVersion?: boolean;
}

function PricingPlanFormModal<TData>({
    selectedPricingPlan,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
    mutatePartial,
    isNewVersion = false,
}: PricingPlanFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [useInRecordsDate, setUseInRecordsDate] = React.useState<string | undefined>(selectedPricingPlan?.valid_from);

    const { parkingId } = useParking();

    // We are in edit mode if we have a selected pricing plan
    // otherwise it means we are creating a new pricing plan

    const { data: pricingPlan } = useGetPricingPlan({
        planId: selectedPricingPlan?.id ?? '',
        parkingId,
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedPricingPlan?.id && isModalOpen },
    });

    const { data: linkedEntities } = useTransformLinkedEntitiesResponse({
        pricingPlanId: selectedPricingPlan?.id ?? '',
        // Only the new-version form renders them, and only once the modal is open
        enabled: isNewVersion && isModalOpen,
    });

    const totalAffectedEntitiesCount = linkedEntities?.total_entities_count ?? 0;
    const pricingPlanIsPublished = pricingPlan?.status === PRICING_PLAN_STATUS.PUBLISHED;

    const onError = useOnError();

    const defaultValues = React.useMemo(
        () =>
            pricingPlan
                ? {
                      ...pricingPlan,
                      status: pricingPlan.status,
                      tax_percent: (Number(pricingPlan.tax_percent) * 100).toFixed(2),
                      fixed_rate: pricingPlan.fixed_rate?.rate_with_tax
                          ? { rate_with_tax: (Number(pricingPlan.fixed_rate.rate_with_tax) / 100).toFixed(2) }
                          : null,
                  }
                : {
                      translations: [],
                      status: PRICING_PLAN_STATUS.DRAFT,
                      capping: CAPPING.NONE,
                  },
        [pricingPlan],
    );

    const form = useForm<PricingPlanFormSchemaValues>({
        resolver: zodResolver(pricingPlanFormSchema),
        defaultValues,
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(defaultValues);
    }, [pricingPlan, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: PricingPlanFormSchemaValues) => {
            // Pin the seconds of the validity range: valid_from starts at second 0 and valid_to ends at
            // the last second of the selected minute, so the plan covers the full chosen minute.
            const withSeconds = (iso: string, seconds: number, milliseconds: number) => {
                const date = new Date(iso);
                date.setSeconds(seconds, milliseconds);
                return date.toISOString();
            };

            const data = {
                ...values,
                valid_from: withSeconds(values.valid_from, 0, 0),
                valid_to: values.valid_to ? withSeconds(values.valid_to, 59, 999) : values.valid_to,
                fixed_rate: values?.fixed_rate?.rate_with_tax
                    ? {
                          rate_with_tax: Number(values.fixed_rate.rate_with_tax) * 100,
                      }
                    : null,
                tax_percent: (Number(values.tax_percent) / 100).toFixed(4),
            } as PricingPlanFormSchemaValues;

            // Partial update for published plans
            if (pricingPlanIsPublished && mutatePartial) {
                mutatePartial(
                    { translations: data.translations },
                    () => setIsModalOpen(false),
                    (error) => onError(error, form.setError),
                );
                return;
            }

            mutate(
                data,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [mutate, mutatePartial, pricingPlanIsPublished, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={className}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader className="flex flex-row gap-2">
                    <SquareIcon icon={icon} />
                    <div>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>{subtitle}</DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <PricingPlanForm
                        form={form}
                        isNewVersion={isNewVersion}
                        setUseInRecordsDate={setUseInRecordsDate}
                        linkedEntities={linkedEntities}
                        isPublished={pricingPlanIsPublished}
                    />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter className="flex w-full flex-row items-baseline justify-between">
                    {isNewVersion && useInRecordsDate && totalAffectedEntitiesCount > 0 && (
                        <div className="flex-1 text-xs text-gray-500">
                            {formatMessage(pricingPlanMessages.usedInRecordsAsOf, {
                                count: totalAffectedEntitiesCount,
                                date: dayjs(useInRecordsDate).format('DD.MM.YYYY HH:mm'),
                            })}
                        </div>
                    )}

                    <div className="flex flex-1 flex-row gap-2">
                        <DialogClose asChild>
                            <Button variant="outline" type="button" className="flex-1">
                                <FormattedMessage
                                    id={generalMessages.cancel.id}
                                    defaultMessage={generalMessages.cancel.defaultMessage}
                                />
                            </Button>
                        </DialogClose>
                        <Button
                            variant="default"
                            disabled={isPending}
                            type="submit"
                            onClick={form.handleSubmit(onSubmit)}
                            className="flex-1"
                        >
                            {isPending && (
                                <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                            )}
                            {title}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default PricingPlanFormModal;
